import { useState } from 'react';
import type { Device, Extension, ProvisioningPreview } from '@arcnet/contracts';
import { useDevices, useOpenProvisioning } from '../../lib/api/hooks-extension.js';
import { usePreviewConfig, useProvisioningRoles } from '../../lib/api/hooks-provisioning.js';
import { DeviceEditor } from '../manage/DevicesTab.js';
import { MobileProvisioning } from './MobileProvisioning.js';
import { VENDOR_LABEL, Window, Preview } from './ProvisioningBits.js';
import { Empty, ErrorBanner, SectionHead, StatusBadge } from '../../components/ui/Primitives.js';
import { Icon } from '../../components/ui/Icon.js';
import { useSession } from '../../lib/auth/session.js';
import { useStepUp, StepUpPrompt } from '../../lib/auth/stepup.js';
import { relativeTime } from '../../lib/format.js';

/**
 * Setting up the phones for ONE seat.
 *
 * The account-wide Provisioning page is the right screen for "which handsets
 * have never fetched a configuration"; it is the wrong one for the job actually
 * in front of somebody, which is a named person, a phone on their desk and a
 * mobile in their hand. That job needs four things together -- the MAC, the
 * make, what the phone will be served, and the app's QR code -- and they were
 * spread across three screens, two of which are in the other console.
 *
 * So this is a view, not a second owner of anything: the handset form is the
 * same component the Handsets tab uses, the window and preview are the same
 * components the Provisioning page uses, and the QR is the same component as
 * before. Nothing here can disagree with them, because nothing here reimplements
 * them.
 */
export function ExtensionProvisioningTab({ tenantId, ext }: {
  tenantId: string; ext: Extension;
}) {
  const can = useSession((s) => s.can);
  const devices = useDevices(tenantId);
  const open = useOpenProvisioning(tenantId);
  const preview = usePreviewConfig(tenantId);
  const roles = useProvisioningRoles();
  const step = useStepUp();
  const [editing, setEditing] = useState<Device | 'new' | null>(null);
  const [shown, setShown] = useState<{ deviceId: string; data: ProvisioningPreview } | null>(null);

  const mayWrite = can('device:write');
  const mayOpen = can('device:provision');
  const mayReveal = can('extension:credentials:reveal');

  /**
   * This seat's handsets, found through the line keys rather than a field on the
   * device: a phone can carry several extensions, and an extension can appear on
   * several phones. "Whose phone is this" has no single answer, and the line key
   * is the only place the real one is recorded.
   */
  const mine = (devices.data?.data ?? [])
    .filter((d) => d.status !== 'retired' && d.lines.some((l) => l.extension_id === ext.id));

  const load = async (deviceId: string, reveal: boolean) => {
    await step.run(async () => {
      const data = await preview.mutateAsync({ deviceId, reveal });
      setShown({ deviceId, data });
      return data;
    });
  };

  return (
    <>
      <SectionHead title="Desk phones"
        sub={`Handsets carrying extension ${ext.number}. The MAC is what identifies a phone when it asks for its configuration.`}
        {...(mayWrite && !editing ? {
          action: (
            <button type="button" className="arc-btn arc-btn-sm" onClick={() => setEditing('new')}>
              <Icon name="plus" size={12} /> Add handset
            </button>
          ),
        } : {})} />

      {devices.error && <ErrorBanner error={devices.error} />}
      {open.error && <ErrorBanner error={open.error} />}
      {preview.error && <ErrorBanner error={preview.error} />}
      <StepUpPrompt step={step} what="Showing a configuration with its SIP password" />

      {editing && (
        <DeviceEditor
          tenantId={tenantId}
          existing={editing === 'new' ? null : editing}
          seedExtensionId={ext.id}
          onDone={() => setEditing(null)}
        />
      )}

      {mine.map((d) => {
        const line = d.lines.find((l) => l.extension_id === ext.id);
        return (
          <div key={d.id} className="arc-card arc-card-body" style={{ marginBottom: 10 }}>
            <div className="arc-row" style={{ gap: 10, alignItems: 'flex-start' }}>
              <div className="arc-stack" style={{ gap: 2 }}>
                <span style={{ fontWeight: 550 }}>
                  {d.label ?? VENDOR_LABEL[d.vendor] ?? 'Handset'}
                </span>
                <span className="arc-subtle" style={{ fontSize: 11.5 }}>
                  {VENDOR_LABEL[d.vendor] ?? d.vendor}{d.model ? ` ${d.model}` : ''}
                  {d.provisioning_role && d.provisioning_role !== 'back_office'
                    ? ` · ${roles.data?.data.find((r) => r.role === d.provisioning_role)?.name ?? d.provisioning_role}`
                    : ''}
                  {/* Which key on the phone this seat sits on. A handset with
                      four lines is common and the seat is rarely the first. */}
                  {line ? ` · line ${line.line_number}${d.lines.length > 1 ? ` of ${d.lines.length}` : ''}` : ''}
                </span>
              </div>
              <div className="arc-spacer" />
              <StatusBadge status={d.status} />
            </div>

            <dl className="arc-kv" style={{ marginTop: 10 }}>
              <dt>MAC address</dt>
              <dd className="arc-mono">
                {d.mac_address ?? <span className="arc-subtle">not set — this phone cannot provision</span>}
              </dd>
              <dt>Last fetched</dt>
              <dd>{d.provisioned_at ? relativeTime(d.provisioned_at) : <span className="arc-subtle">never</span>}</dd>
              <dt>May fetch now</dt>
              <dd><Window device={d} /></dd>
            </dl>

            <div className="arc-row arc-row-actions" style={{ gap: 6, marginTop: 12, flexWrap: 'wrap' }}>
              {mayWrite && (
                <button type="button" className="arc-btn arc-btn-ghost arc-btn-sm"
                  onClick={() => setEditing(d)}>
                  Edit handset
                </button>
              )}
              <button type="button" className="arc-btn arc-btn-ghost arc-btn-sm"
                disabled={preview.isPending}
                onClick={() => {
                  if (shown?.deviceId === d.id) setShown(null);
                  else void load(d.id, false);
                }}>
                {shown?.deviceId === d.id ? 'Hide configuration' : 'Show configuration'}
              </button>
              {mayOpen && (
                <button type="button" className="arc-btn arc-btn-ghost arc-btn-sm"
                  disabled={open.isPending}
                  title="Let this handset fetch its configuration again, for 30 minutes"
                  onClick={() => {
                    if (confirm(`Allow ${d.label ?? d.mac_address ?? 'this handset'} to provision for the next 30 minutes? The URL does not change.`)) {
                      open.mutate(d.id);
                    }
                  }}>
                  Allow provisioning
                </button>
              )}
            </div>

            {shown?.deviceId === d.id && (
              <div style={{
                marginTop: 12, paddingTop: 12, borderTop: '1px solid var(--arc-border)',
              }}>
                <Preview data={shown.data} canReveal={mayReveal}
                  onReveal={() => void load(d.id, true)} />
              </div>
            )}
          </div>
        );
      })}

      {!devices.isLoading && mine.length === 0 && !editing && (
        <Empty icon="phone" title="No handset on this seat"
          hint="Add the phone by its MAC address and it can fetch its own configuration."
          {...(mayWrite ? {
            action: (
              <button type="button" className="arc-btn arc-btn-sm" onClick={() => setEditing('new')}>
                Add handset
              </button>
            ),
          } : {})} />
      )}

      <div style={{ marginTop: 22 }}>
        <MobileProvisioning tenantId={tenantId} ext={ext} />
      </div>

    </>
  );
}
